import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getAuthorizationUrl } from "@/app/servers/api/get-authorization-url";
import { serversKeys } from "@/app/servers/api/query-keys";
import { toolsKeys } from "@/app/tools/api/query-keys";
import type { ScopedBinding } from "@/app/types";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";

type UseOAuthPollingOptions = {
  serverId: string;
  binding?: ScopedBinding;
  onAuthComplete?: () => void;
};

const POLLING_INTERVAL = 1000;
const AGGRESSIVE_POLLING_DURATION = 60_000;
const RELAXED_POLLING_INTERVAL = 30_000;
const HEALTH_REFETCH_DELAY = 3000;

export const useOAuthPolling = ({
  serverId,
  binding = "project",
  onAuthComplete,
}: UseOAuthPollingOptions) => {
  const queryClient = useQueryClient();
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);
  const [isPolling, setIsPolling] = useState(false);
  const pollingStartTimeRef = useRef<number | null>(null);
  const onAuthCompleteRef = useRef(onAuthComplete);
  onAuthCompleteRef.current = onAuthComplete;
  const healthTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearHealthTimers = useCallback(() => {
    for (const id of healthTimersRef.current) {
      clearTimeout(id);
    }
    healthTimersRef.current = [];
  }, []);

  // Clean up timers on unmount
  useEffect(() => clearHealthTimers, [clearHealthTimers]);

  const scheduleHealthRefreshes = useCallback(() => {
    clearHealthTimers();
    for (const delay of [
      HEALTH_REFETCH_DELAY,
      HEALTH_REFETCH_DELAY * 3,
      HEALTH_REFETCH_DELAY * 6,
    ]) {
      healthTimersRef.current.push(
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: serversKeys.health({ serverId, binding, scope }),
          });
        }, delay)
      );
    }
  }, [queryClient, serverId, binding, scope, clearHealthTimers]);

  useQuery({
    queryKey: [
      "oauth-polling",
      scope.organizationId,
      scope.projectId,
      binding,
      serverId,
    ],
    queryFn: async () => {
      const authStatus = await getAuthorizationUrl({
        client,
        serverId,
      });

      if (authStatus.status === "completed") {
        queryClient.setQueryData(
          serversKeys.authStatus({ serverId, binding, scope }),
          { status: "completed" }
        );

        toast.success("OAuth authorization completed successfully!");
        onAuthCompleteRef.current?.();

        queryClient.invalidateQueries({
          queryKey: serversKeys.all({ scope }),
        });
        queryClient.invalidateQueries({
          queryKey: toolsKeys.all({ scope }),
        });
        scheduleHealthRefreshes();

        setIsPolling(false);
        pollingStartTimeRef.current = null;
      }

      if (authStatus.status === "failed") {
        toast.error("OAuth authorization failed. Please try again.");
        setIsPolling(false);
        pollingStartTimeRef.current = null;
      }

      return authStatus;
    },
    enabled: isPolling,
    refetchInterval: () => {
      if (!(isPolling && pollingStartTimeRef.current)) {
        return false;
      }

      const elapsed = Date.now() - pollingStartTimeRef.current;
      return elapsed < AGGRESSIVE_POLLING_DURATION
        ? POLLING_INTERVAL
        : RELAXED_POLLING_INTERVAL;
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const startPolling = useCallback(() => {
    setIsPolling(true);
    pollingStartTimeRef.current = Date.now();
  }, []);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    pollingStartTimeRef.current = null;
    clearHealthTimers();
  }, [clearHealthTimers]);

  return {
    startPolling,
    stopPolling,
    isPolling,
  };
};
